import { Box, Grid, IconButton, InputAdornment, List, ListItem, ListItemText, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AddSkillBookTemplateProps,
  Errors,
  ExternalTrainingSkillBookCreate,
  Field,
  FormValues,
  MultiSelectOptionsProps,
  PayloadPropExternalsTraining
} from "./AddSkillBook.types";
import { Icon, Spinner } from "../../../components";
import { ButtonGroups } from "../../../components/ButtonGroups";
import DataPickerCst from "../../../components/InputCst/DataPickerCst";
import InputCst from "../../../components/InputCst/InputCst";
import InputGroup from "../../../components/InputCst/InputGroup";
import MultiSelectCst from "../../../components/InputCst/MultiSelectCst";
import { CustomFieldResponse, DataResponse, Options } from "../../../components/InputCst/MultiSelectCst/types";
import SelectCst from "../../../components/InputCst/SelectCst";
import { VisuallyHiddenInput } from "../../../components/InputCst/UploadInput";
import OutputMessageModal from "../../../components/Message/OutputMessageModal.component";
import { ModalBasic } from "../../../components/ModalBasic";
import {
  useCapabilitiesOptionsQuery,
  useKnowledgeAreasOptionsQuery,
  useKnowledgeDomainsOptionsQuery,
  usePostExternalTraining,
  useCustomFieldsQuery
} from "../../../queries/externalTrainingSkillbook";
import { useCourseTypesOptionsQuery, useCampaignsOptionsQuery, usePostExternalTrainingConsuntivazione } from "../../../queries/skillbook";
import { useAuthStore } from "../../../stores";
import { form_skillBook_external_training_add } from "../../../utils/form_configurations";
import { handleDownloadFile, handleFileChange, handleMultiDelete, handleMultiFileChange, onSubmitExternal } from "../../../utils/skillBook";
import { SkillBookExternal } from "../AddSkillBook/AddSkillBook.types";


const MAX_SIZE_UPLOAD = 10 * 1024 * 1024; // 10 Mb


const mapCourseItemToFormValues = (course: CourseItem): FormValues => ({
  title: course.title,
  duration: course.duration != null ? String(course.duration) : "",
  durationParticipation: course.durationParticipation != null ? String(course.durationParticipation) : "",
  description: course.description ?? "",
  startDate: course.startDate ?? "",
  endDate: course.endDate ?? "",
  motivation: course.motivation ?? "",
  plan: course.plan ?? "",
  cost: course.cost != null ? String(course.cost) : "",
  dispenser: course.provider ?? "",
  teacher: course.instructor ?? "",
  site: course.location ?? "",
  businessTrip: typeof course.businessTrip === "boolean"
    ? { label: course.businessTrip ? "SI" : "NO", value: course.businessTrip ? "SI" : "NO" }
    : "",
  campaign: course.campaignId ? { label: course.campaign ?? "", value: course.campaignId } : "",
  capability: course.capability?.map(c => ({ label: c.label, value: c.id })) ?? [],
  area_domain: course.area?.flatMap(a =>
    (a.domain || []).map((d: { id?: number; value?: number; label: string }) => ({
      knowledge_area: { label: a.label, value: a.id },
      knowledge_domain: { label: d.label, value: d.id ?? d.value }
    }))
  ) ?? [],
  customFields: course.customFields ?? []
});

const AddSkillBookExternalTrainingTemplate: React.FC<AddSkillBookTemplateProps> = ({ closeModal, initialData }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [formValues, setFormValues] = useState<FormValues>(
    initialData ? mapCourseItemToFormValues(initialData) : {}
  );

  const [errors, setErrors] = React.useState<Errors>({});
  const [knowledgeAreas, setKnowledgeAreas] = useState<MultiSelectOptionsProps[]>([]);
  const [knowledgeDomains, setKnowledgeDomains] = useState<MultiSelectOptionsProps[]>([]);
  const [capabilities, setCapabilities] = useState<MultiSelectOptionsProps[]>([]);
  const [courseTypes, setCourseTypes] = useState<Options[]>([]);
  const [isOutputModalOpen, setIsOutputModalOpen] = React.useState(false);
  const [submitSucceeded,setSubmitSucceeded] = useState<boolean>();
  const [selectedArea, setSelectedArea] = useState<{ isUpdating: boolean; value?: string | number }>({
    isUpdating: false,
    value: ""
  });
  const [formData, setFormData] = useState<Field[]>(form_skillBook_external_training_add.fields);


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
  
    setFormValues(prevValues => ({ ...prevValues, [name]: value }));
  };

  const handleInputChangeWithId = (id:string, value: number | string) => 
    setFormValues(prevValues => ({ ...prevValues, [id]: value }));


  const handleChangeMultiSelect = (
    event: { label: string; value: string | number }[],
    accessorKey: string
  ) => {
    if (!["knowledgeArea", "knowledgeDomain", "capability"].includes(accessorKey)) return;
    let newValues: Options[] = [];

    const formValueArray = (formValues[accessorKey] as Options[]) || [];
    const itemsToAdd = event.filter(ev => !formValueArray.some(fv => fv.value === ev.value));
    const itemsToRemove = formValueArray.filter(fv => !event.some(ev => ev.value === fv.value));

    if (accessorKey === "knowledgeArea" && itemsToAdd[0])
      setSelectedArea({ isUpdating: true, value: itemsToAdd[0].value });

    if (accessorKey === "knowledgeArea" && itemsToRemove[0]) {
      const updatedDomains = knowledgeDomains.filter(domain =>
        itemsToRemove.every(item => domain.parent !== item.value)
      );
      setKnowledgeDomains(updatedDomains);
      setFormValues(prev => ({ ...prev, knowledgeDomain: [] }));
    }

    newValues = [
      ...formValueArray.filter(fv => !itemsToRemove.includes(fv)),
      ...itemsToAdd
    ];

    setFormValues(prev => ({ ...prev, [accessorKey]: newValues }));
  };

  const handleDateChange = (name: string) => (date: Date | null) => { //ONCHANGE DATE FIELD
    // Ignora i valori intermedi durante la digitazione manuale: MUI emette una data
    // valida a ogni cifra dell'anno (es. anno 0002), che essendo controllata
    // riscriverebbe l'input e impedirebbe di completare l'anno. Si committa solo
    // quando la data e' valida e l'anno e' completo (4 cifre).
    if (date && !isNaN(date.getTime()) && date.getFullYear() >= 1000) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: formattedDate
      }));
    }
  };

  useCustomFieldsQuery({
    onSuccess: (data: CustomFieldResponse[]) => {
      const alreadyAdded = formData.some(field => field.type === "text_custom_field");

      if (!alreadyAdded) {
        const header = data.length > 0 ? [{
          name: "header_custom_fields",
          type: "text",
          label: t("custom_fields_section"),
          isHeader: true
        }] : [];

        const customFieldsFromAPI = data.map((item: CustomFieldResponse) => ({
          name: item.fieldName,
          label: item.fieldName,
          type: "text_custom_field",
          required: false,
          isHeader: false,
          fieldId: item.fieldId,
          fieldName: item.fieldName
        }));

        setFormData(prev => [...prev, ...header, ...customFieldsFromAPI]);
      }
    }
  });

  useCapabilitiesOptionsQuery({
    onSuccess: (data: { output: [] }) => {
      const newCapabilities = data.output.map((item: DataResponse) => ({
        label: item.name,
        value: item.id
      }));

      setCapabilities(newCapabilities as MultiSelectOptionsProps[]);
    }
  });

  useCourseTypesOptionsQuery({
    onSuccess: (data: unknown) => {
      const newCourseTypes = (data as DataResponse[]).map((item: DataResponse) => ({
        label: item.name || item.label,
        value: item.id ? String(item.id) : item.value ? String(item.value) : ""
      }));

      return setCourseTypes(newCourseTypes as Options[]);
    }
  });

  // In modifica il corso espone solo la label: pre-seleziona l'option quando le opzioni sono pronte.
  useEffect(() => {
    if (!initialData || courseTypes.length === 0) return;
    setFormValues(prev => {
      if (prev.trainingType) return prev;
      const match = courseTypes.find(opt => opt.label === initialData.trainingMode);
      return match ? { ...prev, trainingType: match } : prev;
    });
  }, [courseTypes, initialData]);

  useKnowledgeAreasOptionsQuery({
    onSuccess: (data: { output: DataResponse[] }) => {
      const newKnowledgeArea = data.output.map((item: DataResponse) => ({
        label: item.name,
        value: item.id
      }));

      setKnowledgeAreas(newKnowledgeArea as MultiSelectOptionsProps[]);
    }
  });

  useKnowledgeDomainsOptionsQuery({
    enabled: selectedArea.isUpdating && !!selectedArea.value,
    onError: (error) => {
      console.error("Error fetching data Domains:", error);
    },
    onSuccess: (data: { output: DataResponse[] }) => {
      const areaId = selectedArea.isUpdating && selectedArea.value ? selectedArea.value.toString() : "";
      if (!areaId) return;
      const newKnowledgeDomain = data.output.map((item: DataResponse) => ({
        label: item.name,
        parent: selectedArea.value,
        value: item.id
      }));

      // newKnowledgeDomain.push(...knowledgeDomains as []);

      setKnowledgeDomains(newKnowledgeDomain as MultiSelectOptionsProps[]);
      setSelectedArea({ isUpdating: false });
    },
    knowledgeAreaId: selectedArea.isUpdating && selectedArea.value ? selectedArea.value.toString() : ""
  });

  const getOptions = (type: string) => {
    switch (type) {
    case "campaign": return campaignOptions;
    case "knowledgeArea": return knowledgeAreas || [];
    case "knowledgeDomain": return knowledgeDomains || [];
    case "capability": return capabilities || [];
    case "trainingType": return courseTypes || [];
    case "businessTrip":
      return [
        { label: "SI", value: "SI" },
        { label: "NO", value: "NO" }
      ];
    default: return [];
    }
  };


  const onCancel = () => {
    setFormValues({});
    closeModal();
  };

  const handleCloseWithRefresh = () => {
    setIsOutputModalOpen(false);
    closeModal();
  };

  const { mutate: postExternalTraining, isPending } = usePostExternalTraining({
    onSuccess: (data) => {
      setSubmitSucceeded(true);
      setIsOutputModalOpen(true);
      console.log("✅ Iscrizione creata con successo", data);
    },
    onError: (error) => {
      setSubmitSucceeded(false);
      setIsOutputModalOpen(true);
      console.error("❌ Errore durante la creazione dell'iscrizione:", error);
    }
  });

  const { mutate: updateExternalTraining } = usePostExternalTrainingConsuntivazione({
    onSuccess: () => {
      setSubmitSucceeded(true);
      setIsOutputModalOpen(true);
    },
    onError: (error) => {
      setSubmitSucceeded(false);
      setIsOutputModalOpen(true);
      console.error("Errore durante la modifica:", error);
    }
  });
  const validateForm = () => {
    let newErrors: Errors = {};
    const today = new Date();
    const startDate = new Date(formValues.startDate as string);
    const endDate = new Date(formValues.endDate as string);
    const validationStartDate = new Date(formValues.validationStartDate as string);
    const validationEndDate = new Date(formValues.validationEndDate as string);
    formData.forEach((field: Field) => {
      if (field.required && !formValues[field.name]) {
        newErrors[field.name] = t(`${field.name}_required`);
      }
    });
    // if (startDate > today) {
    //   newErrors.startDate = t("date_in_future_error");
    // }
    // if (endDate > today) {
    //   newErrors.endDate = t("date_in_future_error");
    // }
    if (endDate < startDate) {
      newErrors.endDate = t("endDate_before_startDate_error");
    }
    if (validationEndDate < today) {
      newErrors.validationEndDate = t("certificate_expired");
    }
    if (validationEndDate < validationStartDate) {
      newErrors.validationEndDate = t("validationEndDate_before_validationStartDate_error");
    }
    if (validationStartDate < endDate) {
      newErrors.validationStartDate = t("validation_start_date_before_end_course");
    }

    if (formValues.duration && (Number(formValues.duration) < 0)) {
      newErrors.duration = t("duration_negative_value_error");
    }
    if (formValues.duration && isNaN(Number(formValues.duration))) {
      newErrors.duration = t("duration_type_value_error");
    }
    if (!formValues.businessTrip ) {
      newErrors.businessTrip = t("duration_type_value_error");
    }

    if (formValues.durationParticipation && (Number(formValues.durationParticipation) < 0)) {
      newErrors.durationParticipation = t("duration_participation_negative_value_error");
    }
    if (formValues.durationParticipation && isNaN(Number(formValues.durationParticipation))) {
      newErrors.durationParticipation = t("duration_participation_type_value_error");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const [campaignOptions, setCampaignOptions] = useState<Options[]>([]);
  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";

  useCampaignsOptionsQuery({
    enabled: Boolean(corporateId),
    onError: console.error,
    onSuccess: (data: { output: DataResponse[] }) => {
      const opts = data.output.map(item => ({
        label: item.name ?? "",
        value: String(item.id)
      }));
      setCampaignOptions(opts);
    }
  });


  const learnerId = useAuthStore(state => state.userData?.id_user) ?? "";
  const handleConfirmExternalTraining = () => {
    const customFieldsData = formData
      .filter(field => field.type === "text_custom_field")
      .map(field => ({
        fieldId: field.fieldId!,
        fieldName: field.name!,
        fieldValue: String(formValues[field.name] ?? "")
      }));

    const externalTrainingData: ExternalTrainingSkillBookCreate = {
      title: String(formValues.title ?? ""),
      durationMinutes: Number(formValues.duration ?? 0),
      durationParticipation: Number(formValues.durationParticipation ?? 0),
      trainingMode: (formValues.trainingType as { label: string; value: string })?.value ?? "",
      description: String(formValues.description ?? ""),
      startDate: String(formValues.startDate ?? ""),
      endDate: String(formValues.endDate ?? ""),
      code: String(formValues.code ?? ""),
      additionalNotes: String(formValues.notes ?? ""),
      area: Array.isArray(formValues.area_domain)
        ? (formValues.area_domain as {
            knowledge_area: { label: string; value: number | string };
            knowledge_domain?: { label: string; value: number | string };
          }[]).map(item => ({
          id: Number(item.knowledge_area.value),
          label: item.knowledge_area.label,
          domain: item.knowledge_domain
            ? [{
              id: Number(item.knowledge_domain.value),
              label: item.knowledge_domain.label
            }]
            : []
        }))
        : [],

      capability: Array.isArray(formValues.capability)
        ? (formValues.capability as { label: string; value: string }[]).map(c => ({
          id: c.value,
          label: c.label
        }))
        : [],

      sourceId: formValues.sourceId ?? "",
      source: formValues.source ?? null,
      campaign: (formValues.campaign as { value: string })?.value ?? "",
      campaignId: Number((formValues.campaign as { value: string })?.value) ?? null,
      credits: Number(formValues.credits ?? 0),
      points: Number(formValues.points ?? 0),
      provider: String(formValues.dispenser ?? ""),
      instructor: String(formValues.teacher ?? ""),
      motivation: String(formValues.motivation ?? ""),
      location: String(formValues.site ?? ""),
      businessTrip: (formValues.businessTrip as { value?: string })?.value === "SI",
      cost: Number(String(formValues.cost).replace(",", ".")),
      status: String(formValues.status ?? "Pending"),
      year: Number(formValues.year ?? new Date().getFullYear()),
      corporateId: Number(corporateId ?? 0),
      learnerId: Number(learnerId ?? 0),
      plan: String(formValues.plan?? ""),
      customFields: customFieldsData
    };

    if (!validateForm()) {
      setErrors((prevErrors) => ({ ...prevErrors }));
      return;
    }

    const payload: PayloadPropExternalsTraining = {
      data: externalTrainingData,
      attachments: formValues.attachments as string[]
    };

    if (initialData?.id) {
      onSubmitExternal(
        formValues as unknown as SkillBookExternal,
        updateExternalTraining as unknown as (data: FormData) => void,
        initialData.id
      );
    } else {
      postExternalTraining(payload);
    }
  };

  const formatCost = () => {
    const value = formValues.cost as string | number;

    if (value !== undefined && value !== null && !isNaN(Number(value))) {
      const formatted = parseFloat(value.toString()).toFixed(2).replace(".", ",");
      setFormValues(prev => ({
        ...prev,
        cost: formatted
      }));
    }
  };

  if(isPending) return <Spinner/>;

  return (
    <>
      <Box
        sx={ {
          flexDirection: "column",
          display: "flex",
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "&.MuiFormHelperText-contained Mui-required": {
            m: 0
          }
        } }
      >
        <Grid 
          container 
          spacing="0.65rem"
        >
          { formData.map((data, index) => {
            if (data.isHeader) {
              return (
                <Grid 
                  key={ index } 
                  xs={ 12 }
                  sx={ { ml: "0.65rem" } }
                >
                  <Box sx={ { mt: "1rem" } }>
                    <Typography 
                      variant="h6" 
                      sx={ { 
                        color: theme.customColors.textWidget, 
                        fontWeight: "bold",
                        lineheight: "1.5rem"
                      } }>
                      { t(data.label) }
                    </Typography>
                  </Box>
                </Grid>
              );
            }
            switch (data.type) {
            
            case "textarea":
              return (
                <Grid key={ index } item xs={ 12 }>
                  <InputCst
                    name={ data.name }
                    label={ t(`${data.name}_field`) }
                    value={ formValues[data.name] as string ?? "" }
                    onChange={ handleInputChange }
                    type={ data.type }
                    multiline
                    rows={ 4 }
                    fullWidth
                    error={ !!errors[data.name] }
                    helperText={ errors[data.name] }
                  />
                </Grid>
              );
            case "text":
            case "number":        
              if (data.name === "cost") {
                return (
                  <Grid key={ index } item xs={ 12 } sm={ 6 }>
                    <TextField
                      fullWidth
                      name={ t("cost") }
                      label={ t(`${data.name}_field`) }
                      value={ formValues.cost || "" }
                      onChange={ (e) => { 
                        if(isNaN(Number(e.target.value))) return;
                        handleInputChangeWithId(data.name, e.target.value); 
                      }
                      }
                      onBlur={ formatCost }
                      error={ !!errors.cost }
                      helperText={ errors.cost }
                      placeholder="0,00"
                      InputProps={ {
                        startAdornment:
                        !formValues.cost ? (
                          <InputAdornment position="start">
                            <span 
                              style={ { color: theme.customColors.textWidget, fontSize: "1rem"  } }
                            >
                              €
                            </span>
                          </InputAdornment>
                        ) : (
                          <InputAdornment position="start">
                            <span 
                              style={ { color: theme.customColors.textWidget, fontSize: "1rem"  } }
                            >
                              €
                            </span>
                          </InputAdornment>
                        )
                      } }
                      sx={ {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "0.5rem",
                          height: "2.27rem",
                          "& .MuiOutlinedInput-input": {
                            height: "100%",
                            boxSizing: "border-box",
                            paddingTop: 0,
                            paddingBottom: 0
                          },
                          "& fieldset": {
                            borderColor: theme.customColors.border
                          }
                        },
                        "& .MuiFormLabel-root": {
                          color: theme.customColors.textPrimary
                        },
                        "& .MuiInputBase-root.Mui-focused fieldset": {
                          color: theme.customColors.borderPrimaryCta,
                          borderColor: theme.customColors.borderPrimaryCta
                        },
                        "& .MuiInputLabel-root.Mui-focused.MuiInputLabel-shrink": {
                          color: theme.customColors.borderPrimaryCta
                        },
                        "& .MuiSvgIcon-root": {
                          color: theme.customColors.textPrimary
                        }
                      } }

                    />

                  </Grid>
                );
              }
              return (
                <Grid key={ index } item xs={ 12 } sm={ 6 }>
                  <InputCst
                    name={ data.name }
                    type={ data.type }
                    label={ t(`${data.name}_field`) }
                    value={ formValues[data.name] as string ?? "" }
                    required={ data.required }
                    disabled={ false }
                    maxValue={ data.maxValue }
                    onChange={ handleInputChange }
                    error={ !!errors[data.name] }
                    errorMessage={ errors[data.name] }
                  />
                </Grid>
              );
            case "text_custom_field":
              return (
                <Grid key={ index } item xs={ 12 } sm={ 6 }>
                  <InputCst
                    name={ data.name }
                    type={ data.type }
                    label={ t(`${data.name}`) }
                    value={ formValues[data.name] as string ?? "" }
                    required={ data.required }
                    disabled={ false }
                    maxValue={ data.maxValue }
                    onChange={ handleInputChange }
                    error={ !!errors[data.name] }
                    errorMessage={ errors[data.name] }
                  />
                </Grid>
              );
            case "select":
              return (
                <Grid key={ index } item xs={ 12 } sm={ 6 }>
                  <SelectCst
                    options={ getOptions(data.name) as Options[] }
                    value={ formValues[data.name] ? (formValues[data.name] as { value: string }).value as string : "" }
                    onChange={ (e: ChangeEvent<{ value: string | number }>) => {
                      const value = e.target.value;
                      const labelElement = document.querySelector(`li[data-value="${value}"]`) as HTMLElement;
                      const label = labelElement?.getAttribute("data-label") || "";
                      setFormValues((prevState) => ({
                        ...prevState,
                        [data.name]: {
                          label: label,
                          value: e.target.value
                        }
                      }));

                    } }
                    name={ data.name }
                    label={ t(`${data.name}_field`) }
                    required={ data.required }
                    error={ !!errors[data.name] }
                    errorMessage={ t(`${data.name}_required`) }
                    sxTypography={ { color: theme.customColors.exploreCardText } }
                  />
                </Grid>
              );
              // case "boolean-select":
              //   return (
              //     <Grid key={ index + "-boolean-select" } item xs={ 12 } sm={ 6 }>
              //       <SelectCst
              //         key={ data.name }
              //         label={ t(`${data.name}_field`) }
              //         value={ formValues[data.name] ? (formValues[data.name] as { value: string }).value as string : "" }
              //         onChange={ (e: any) =>
              //           dispatch({
              //             type: "SET_INPUT_VALUES",
              //             payload: {
              //               key: col.accessorKey,
              //               value: e === "true",
              //             },
              //           })
              //         }
              //         options={[
              //           { label: "Si", value: "true" },
              //           { label: "No", value: "false" },
              //         ]}
              //         required={col.required}
              //         error={!!errors["business_trip"]}
              //         errorMessage={`${t(`validation.${errors["business_trip"]}`)}`}
              //       />
              //     </Grid>
              //  );
            case "multi-select":
              return (
                <Grid key={ index } item xs={ 12 } sm={ 6 }>
                  <MultiSelectCst
                    options={ getOptions(data.name) }
                    selectedOptions={ (formValues[data.name] as MultiSelectOptionsProps[]) || [] }
                    handleChange={ (e: MultiSelectOptionsProps[]) => handleChangeMultiSelect(e, data.name) }
                    label={ t(`${data.name}_field`) }
                    disabled={ data.name === "knowledgeDomain" ? getOptions(data.name).length === 0 : false }
                    sxTypography={ { color: theme.customColors.exploreCardText } }
                  />
                </Grid>
              );
            case "date":
              return (
                <Grid key={ index } item xs={ 12 } sm={ 6 }>
                  <DataPickerCst
                    id={ data.name }
                    label={ t(`${data.name}_field`) }
                    name={ data.name }
                    value={ formValues[data.name] ? new Date(formValues[data.name] as string) : null }
                    onChange={ handleDateChange(data.name) }
                    required={ data.required }
                    error={ !!errors[data.name] }
                    errorMessage={ errors[data.name] }
                  />
                </Grid>
              );
            case "groups":
              return (
                <Grid key={ index } item xs={ 12 }>
                  <InputGroup
                    label={ t(`${data.name}_field`) }
                    fields={ [
                      {
                        label: "Knowledge Area",
                        accessorKey: "knowledge_area",
                        type: "select",
                        labelHeader: {
                          en: "Knowledge Area",
                          it: "Area di Conoscenza"
                        },
                        options: getOptions("knowledgeArea"),
                        required: false
                      },
                      {
                        label: "Knowledge Domain",
                        accessorKey: "knowledge_domain",
                        type: "select",
                        labelHeader: {
                          en: "Knowledge Domain",
                          it: "Dominio di Conoscenza"
                        },
                        options: getOptions("knowledgeDomain"),
                        dependentOn: "knowledge_area",
                        required: false
                      }
                    ] }
                    onChange={ (newValue) => setFormValues((prevState) => ({
                      ...prevState,
                      [data.name]: newValue
                    })) }
                    value={ formValues[data.name] || [] }
                    onFieldChange={ (e) =>
                      setSelectedArea({ isUpdating: true, value: e.toString() })
                    }
                  />
                </Grid>
              );
            case "file":
              return (
                <Grid key={ index } item xs={ 12 } sm={ 6 }>
                  <VisuallyHiddenInput
                    name={ data.name }
                    label={ formValues[data.name] ? `${t("file_uploaded")} ${formValues["file_name"]}` : t(`${data.name}_field`) }
                    type={ data.type }
                    accept=".pdf, .jpg, .jpeg, .png, .csv, .xls, .xlsx, .doc, .docx"
                    onChange={ (e) => handleFileChange(e, data.name, MAX_SIZE_UPLOAD, 1024, errors, setErrors, setFormValues) }
                    error={ !!errors["file"] }
                    errorMessage={ t(errors["file"]) }
                  />
                </Grid>
              );
            case "multi-file":
              return (
                <Grid key={ data.name } item xs={ 12 } sm={ 12 }>
                  <VisuallyHiddenInput
                    name={ data.name }
                    label={ t(`${data.name}_field`) }
                    type="file"
                    accept=".pdf, .jpg, .jpeg, .png"
                    onChange={ (e) => {
                      handleMultiFileChange(
                        e,
                        data.name,
                        MAX_SIZE_UPLOAD,
                        1024,
                        errors,
                        setErrors,
                        setFormValues
                      );
                    }
                    }
                    error={ !!errors["attachments"] }
                    errorMessage={ t(`${ errors["attachments"] }`) }
                  />
                  <List dense>
                    { Array.isArray(formValues["attachments"]) &&
                    formValues["attachments"].map((file, index) => (
                      <ListItem
                        key={ index }
                        sx={ {
                          border: "1px solid #ccc",
                          borderRadius: "0.5rem"
                        } }
                        secondaryAction={
                          <>
                            <IconButton
                              edge="end"
                              aria-label="open"
                              onClick={ () => {
                                handleDownloadFile(file);
                              } }
                            >
                              <Icon
                                className="icon icon-download"
                                icon={ "download" }
                                width={ "20px" }
                                height={ "20px" }
                                color={ theme.customColors.textWidget }
                              />
                            </IconButton>

                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={ () => {
                                handleMultiDelete(
                                  data.name,
                                  file.name,
                                  setFormValues
                                );
                              } }
                            >
                              <Icon
                                icon={ "Icons_delete" }
                                size={ 20 }
                              />
                            </IconButton>

                          </>
                        }
                      >
                        <ListItemText primary={ file.name } />
                      </ListItem>
                    )) }
                  </List>
                </Grid>
              );
            default:
              return null;
            }
          }) }
        </Grid>
     
        <ModalBasic
          open={ isOutputModalOpen }
          title={ "" }
          content={ <OutputMessageModal success={ submitSucceeded ?? false } closeModal={ handleCloseWithRefresh } /> }
          size={ { height: "auto", maxHeight: "90vh", width: { xs: "100vw", sm: "fit-content" } } }
          handleClose={ handleCloseWithRefresh }
        />
      </Box>
      <ButtonGroups onClickCancel={ onCancel } onClickConfirm={ handleConfirmExternalTraining } />
    </>
  );
};

export default AddSkillBookExternalTrainingTemplate;
